/**
 * laborMethods.js — canonical labor method vocabulary.
 *
 * ONE source of truth for the four methods. Imported by:
 *   - Settings → Margins & Labor (MarginsPage.js)
 *   - Settings → Quote Areas (QuoteAreasPage.js)
 *   - resolveLaborCost.js (the pricing resolver)
 *   - OnboardingWizard.js
 *
 * DO NOT redeclare this list in another file.
 *
 * Canonical shape (labor_rates on the settings record):
 *   labor_rates: {
 *     piece: {
 *       sq_ft:     number | null,   // dollars per sq ft
 *       linear_ft: number | null,   // dollars per linear ft
 *     },
 *     set:    number | null,        // dollars per set
 *     hourly: number | null,        // dollars per hour
 *     day:    number | null,        // dollars per day
 *   }
 *
 * "Piece rate" is the umbrella — both sq_ft and linear_ft live under it.
 * The product's calcMethod determines which rate applies to a given line.
 * A dealer sets BOTH; the system picks the right one per product.
 */

/**
 * The four canonical labor methods.
 * `autoCalculable`: can derive labor cost from the line's quantity alone.
 */
export const LABOR_METHODS = [
  { id: 'piece', label: 'Piece Rate', autoCalculable: true },
  { id: 'set', label: 'Set Rate', autoCalculable: false },
  { id: 'hourly', label: 'Hourly', autoCalculable: false },
  { id: 'day', label: 'Day Rate', autoCalculable: false },
];

/**
 * Whether a method can auto-calculate labor from the line's quantity.
 * Only 'piece' can — it multiplies rate × qty.
 * hourly/day/set need per-line inputs (LAB-6, not yet built).
 */
export function isAutoCalculable(method) {
  return method === 'piece';
}

/**
 * Normalise a legacy laborMethod value.
 * 'linear_ft' was a separate method before LAB-A; it is now a measure under 'piece'.
 * This is the ONE place that mapping lives.
 */
export function normaliseLaborMethod(method) {
  if (method === 'linear_ft') return 'piece';
  return method || null;
}

/**
 * Normalise legacy labor_rates into the canonical shape.
 *
 * Legacy shape:  { per_sq_ft: "1", per_linear_ft: ".75", hourly: "", day: "" }
 * Canonical:     { piece: { sq_ft: "1", linear_ft: ".75" }, set: null, hourly: null, day: null }
 *
 * Rules:
 *   - If `piece` key already exists with the new shape → use it.
 *   - If `per_sq_ft` / `per_linear_ft` present → migrate into piece.sq_ft / piece.linear_ft.
 *   - BOTH can be set at once — that is the supported configuration (Sarah ruling).
 *     No warning, no preference, no discard.
 *   - Keep legacy keys alongside new ones (do NOT drop them).
 *
 * @param {object} raw — the stored labor_rates object
 * @returns {object} — canonical shape + legacy keys preserved
 */
export function normaliseLaborRates(raw) {
  if (!raw || typeof raw !== 'object') {
    return { piece: { sq_ft: null, linear_ft: null }, set: null, hourly: null, day: null };
  }

  // IDI-374: since #378 (IDI-260) the settings GET projects labor_rates as a
  // NAMED LIST ([{id, name, category, rate, sub_area_ids}]) via the backend's
  // normalizeLaborRatesList. The old branches below only understood the scalar
  // object, so a list fell through to "legacy", read nothing, and returned
  // all-null — the Margins page then rendered blank fields AND Save wrote those
  // blanks back over the real rates (data loss). Map the list back to the scalar
  // shape the page renders, keyed on the stable migrated ids the backend emits.
  if (Array.isArray(raw)) {
    const byId = {};
    for (const item of raw) {
      if (item && typeof item === 'object' && item.id != null) {
        byId[item.id] = item.rate ?? null;
      }
    }
    const clean = (v) => (v != null && v !== '' ? v : null);
    return {
      piece: {
        sq_ft: clean(byId.migrated_piece_sqft),
        linear_ft: clean(byId.migrated_piece_linear_ft),
      },
      set: clean(byId.migrated_set),
      hourly: clean(byId.migrated_hourly),
      day: clean(byId.migrated_day),
    };
  }

  // Already in canonical shape (piece has sq_ft/linear_ft)?
  if (raw.piece && typeof raw.piece === 'object' && 'sq_ft' in raw.piece) {
    return {
      piece: raw.piece,
      set: raw.set ?? null,
      hourly: raw.hourly ?? null,
      day: raw.day ?? null,
      // Preserve legacy keys.
      per_sq_ft: raw.per_sq_ft,
      per_linear_ft: raw.per_linear_ft,
    };
  }

  // Legacy shape → normalise. BOTH rates survive.
  const sqFt = (raw.per_sq_ft != null && raw.per_sq_ft !== '') ? raw.per_sq_ft : null;
  const linFt = (raw.per_linear_ft != null && raw.per_linear_ft !== '') ? raw.per_linear_ft : null;

  return {
    piece: { sq_ft: sqFt, linear_ft: linFt },
    set: raw.set ?? null,
    hourly: (raw.hourly != null && raw.hourly !== '') ? raw.hourly : null,
    day: (raw.day != null && raw.day !== '') ? raw.day : null,
    // Preserve legacy keys.
    per_sq_ft: raw.per_sq_ft,
    per_linear_ft: raw.per_linear_ft,
  };
}

/**
 * Serialise canonical labor_rates for the settings write.
 * Writes BOTH the new shape AND the legacy keys so existing readers keep working.
 *
 * @param {object} canonical — { piece: { sq_ft, linear_ft }, set, hourly, day }
 * @returns {object} — ready for the settings PUT
 */
export function serialiseLaborRates(canonical) {
  if (!canonical) return { piece: { sq_ft: null, linear_ft: null }, set: null, hourly: null, day: null, per_sq_ft: '', per_linear_ft: '' };

  const piece = canonical.piece || { sq_ft: null, linear_ft: null };
  const result = {
    piece,
    set: canonical.set || null,
    hourly: canonical.hourly || null,
    day: canonical.day || null,
    // Legacy keys for backward compat.
    per_sq_ft: piece.sq_ft || '',
    per_linear_ft: piece.linear_ft || '',
  };

  return result;
}
